import type { WorkOrderId, WorkRequestId } from "../../lib/workIds";

type ActivityWorkOrder = {
  id: WorkOrderId;
  number: string;
  title: string;
  createdOn?: string | null;
  createdBy?: string | null;
  completedOn?: string | null;
  completedBy?: string | null;
};

type ActivityRequest = {
  id: WorkRequestId;
  requestNumber: string;
  title: string;
  submittedAt: string;
  requester: { name: string };
  reviewedAt: string | null;
  reviewer: { name: string } | null;
  status: { code: string };
};

export type AssetActivityEvent = {
  key: string;
  at: string;
  action: string;
  reference: string;
  title: string;
  actor: string | null;
  target:
    | { kind: "WORK_ORDER"; id: WorkOrderId }
    | { kind: "REQUEST"; id: WorkRequestId };
};

const REVIEW_ACTION: Record<string, string> = {
  APPROVED: "Request approved",
  DECLINED: "Request declined",
  CANCELED: "Request canceled",
};

/**
 * Derives the asset's Recent Activity from timestamps the Work Order and
 * Request records already carry — no activity is invented beyond them.
 * Newest first.
 */
export function buildAssetActivity(
  workOrders: ActivityWorkOrder[],
  requests: ActivityRequest[],
  limit = 25,
): AssetActivityEvent[] {
  const events: AssetActivityEvent[] = [];

  for (const workOrder of workOrders) {
    const target = { kind: "WORK_ORDER" as const, id: workOrder.id };
    if (workOrder.createdOn) {
      events.push({
        key: `${workOrder.id}:created`,
        at: workOrder.createdOn,
        action: "Work Order created",
        reference: workOrder.number,
        title: workOrder.title,
        actor: workOrder.createdBy ?? null,
        target,
      });
    }
    if (workOrder.completedOn) {
      events.push({
        key: `${workOrder.id}:completed`,
        at: workOrder.completedOn,
        action: "Work Order completed",
        reference: workOrder.number,
        title: workOrder.title,
        actor: workOrder.completedBy ?? null,
        target,
      });
    }
  }

  for (const request of requests) {
    const target = { kind: "REQUEST" as const, id: request.id };
    events.push({
      key: `${request.id}:submitted`,
      at: request.submittedAt,
      action: "Request submitted",
      reference: request.requestNumber,
      title: request.title,
      actor: request.requester.name,
      target,
    });
    if (request.reviewedAt) {
      events.push({
        key: `${request.id}:reviewed`,
        at: request.reviewedAt,
        action: REVIEW_ACTION[request.status.code] ?? "Request reviewed",
        reference: request.requestNumber,
        title: request.title,
        actor: request.reviewer?.name ?? null,
        target,
      });
    }
  }

  return events
    .filter((event) => !Number.isNaN(new Date(event.at).valueOf()))
    .sort((left, right) => Date.parse(right.at) - Date.parse(left.at))
    .slice(0, limit);
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 60 * 60 * 1000],
  ["month", 30 * 24 * 60 * 60 * 1000],
  ["week", 7 * 24 * 60 * 60 * 1000],
  ["day", 24 * 60 * 60 * 1000],
  ["hour", 60 * 60 * 1000],
  ["minute", 60 * 1000],
];

export function formatActivityTime(iso: string, now: Date = new Date()) {
  const date = new Date(iso);
  const elapsed = date.valueOf() - now.valueOf();
  const relativeFormat = new Intl.RelativeTimeFormat(undefined, {
    numeric: "auto",
  });
  const [unit, unitMs] = RELATIVE_UNITS.find(
    ([, ms]) => Math.abs(elapsed) >= ms,
  ) ?? ["minute", 60 * 1000];
  return {
    relative: relativeFormat.format(Math.round(elapsed / unitMs), unit),
    absolute: new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date),
  };
}
